import { useOutletContext } from 'react-router-dom'

import { Section } from '@factorialco/deprecated-design-system'
import { ProjectDetailsPageSettingsPageBudgetSectionFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ProjectBudgetForm, {
  IProjectBudgetValues,
} from 'modules/project_management/components/forms/ProjectBudgetForm'
import {
  useCreateBudgetStrategy,
  useDeleteBudgetStrategy,
  useUpdateBudgetStrategy,
} from 'modules/project_management/hooks/planningMutations/budgetStrategyHooks'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import {
  getDecimalHoursFromMinutes,
  getMinutesFromDecimalHours,
} from 'modules/project_management/lib/formatHours'
import { isValidBudgetType } from 'modules/project_management/lib/utils'

export const FixedBudgetSection: React.FC<{
  project: ProjectDetailsPageSettingsPageBudgetSectionFragment
}> = ({ project }) => {
  const { company } = useCurrentSession()

  const { createBudgetStrategy } = useCreateBudgetStrategy(company.id)
  const { updateBudgetStrategy } = useUpdateBudgetStrategy()
  const { deleteBudgetStrategy } = useDeleteBudgetStrategy()

  // Helper to get current budget type
  const getCurrentBudgetType = (): budgetTypesEnum => {
    if (project.budgetStrategy && isValidBudgetType(project.budgetStrategy.budgetType)) {
      return project.budgetStrategy.budgetType
    }
    return budgetTypesEnum.withoutBudget
  }

  // Different options for mutation
  const getMutation = (data: IProjectBudgetValues): Promise<UnhandledResult<Result<unknown>>> => {
    const currentBudgetType = getCurrentBudgetType()
    const targetBudgetType = data.budgetType
    const plannedCents =
      Number(data.plannedCents) === 0 ? undefined : Math.round(Number(data.plannedCents) * 100)
    const plannedMinutes =
      Number(data.plannedMinutes) === 0
        ? undefined
        : getMinutesFromDecimalHours(Number(data.plannedMinutes))
    const feeAmountCents = data.feeAmountCents
      ? Math.round(Number(data.feeAmountCents) * 100)
      : undefined

    // Handle deletion (withoutBudget)
    if (targetBudgetType === budgetTypesEnum.withoutBudget) {
      if (!project.budgetStrategy) {
        throw new Error('No budget to delete')
      }
      return deleteBudgetStrategy({ id: project.budgetStrategy.id })
    }

    // Handle updates (same type)
    if (currentBudgetType === targetBudgetType) {
      switch (targetBudgetType) {
        case budgetTypesEnum.projectFixedCost: {
          const budgetStrategy = project.budgetStrategy
          if (!budgetStrategy) {
            throw new Error('No project fixed cost to update')
          }
          return updateBudgetStrategy({
            id: budgetStrategy.id,
            budgetAllocation: undefined,
            budgetStrategyType: budgetTypesEnum.projectFixedCost,
            plannedCents,
            feeAmountCents,
          })
        }
        case budgetTypesEnum.totalBudget: {
          const budgetStrategy = project.budgetStrategy
          if (!budgetStrategy) {
            throw new Error('No total budget to update')
          }
          return updateBudgetStrategy({
            id: budgetStrategy.id,
            budgetAllocation: undefined,
            budgetStrategyType: budgetTypesEnum.totalBudget,
            plannedCents,
            plannedMinutes,
          })
        }
        default:
          throw new Error('Cannot update No budget option')
      }
    }

    // Handle creation/switching between types
    switch (targetBudgetType) {
      case budgetTypesEnum.projectFixedCost:
        return createBudgetStrategy({
          projectId: project.id,
          budgetStrategyType: budgetTypesEnum.projectFixedCost,
          budgetAllocation: undefined,
          plannedCents,
          feeAmountCents,
        })
      case budgetTypesEnum.totalBudget:
        return createBudgetStrategy({
          projectId: project.id,
          budgetStrategyType: budgetTypesEnum.totalBudget,
          budgetAllocation: undefined,
          plannedCents,
          plannedMinutes,
        })
      default:
        throw new Error(`Unknown budget type: ${targetBudgetType}`)
    }
  }

  const { hasCostsFeature } = useOutletContext<{
    hasCostsFeature: boolean
  }>()

  const getProjectBudgetDefaultValues = () => {
    const currentBudgetType = getCurrentBudgetType()
    const baseCurrency = { currencyType: project.legalEntity.currency }

    switch (currentBudgetType) {
      case budgetTypesEnum.projectFixedCost: {
        const budgetStrategy = project.budgetStrategy
        if (!budgetStrategy) throw new Error('No fixed price budget found')
        return {
          budgetType: budgetTypesEnum.projectFixedCost,
          budgetAllocation: null,
          budgetAllocationCents: null,
          feeAmountCents: budgetStrategy.feeAmountCents
            ? budgetStrategy.feeAmountCents / 100
            : undefined,
          plannedCents: budgetStrategy.plannedCents ? budgetStrategy.plannedCents / 100 : undefined,
          plannedMinutes: null,
          ...baseCurrency,
        }
      }
      case budgetTypesEnum.totalBudget: {
        const budgetStrategy = project.budgetStrategy
        if (!budgetStrategy) {
          throw new Error('No total budget found')
        }
        return {
          budgetType: budgetTypesEnum.totalBudget,
          budgetAllocation: null,
          budgetAllocationCents: null,
          feeAmountCents: null,
          plannedCents: budgetStrategy.plannedCents ? budgetStrategy.plannedCents / 100 : undefined,
          plannedMinutes: budgetStrategy.plannedMinutes
            ? getDecimalHoursFromMinutes(budgetStrategy.plannedMinutes)
            : undefined,
          ...baseCurrency,
        }
      }
      default:
        return {
          budgetType: budgetTypesEnum.withoutBudget,
          budgetAllocation: undefined,
          feeAmountCents: undefined,
          plannedCents: undefined,
          plannedMinutes: undefined,
          ...baseCurrency,
        }
    }
  }

  const { values, errors, setValues, setErrors, handleSubmit, dirty } =
    useForm<IProjectBudgetValues>({
      defaultValues: getProjectBudgetDefaultValues(),
      enableReinitialize: true,
      validateOnChange: true,
      validateOnBlur: true,
      validate: (data) => {
        const errorValidation: FormErrors<IProjectBudgetValues> = {}
        if (data.budgetType === budgetTypesEnum.projectFixedCost && data.plannedCents) {
          if (data.plannedCents < 0) {
            errorValidation.plannedCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
          if (isNaN(data.plannedCents)) {
            errorValidation.plannedCents = i18n.t(
              'project_management.planning.validation_errors.field_is_number'
            )
          }
        }
        if (data.budgetType === budgetTypesEnum.projectFixedCost && data.feeAmountCents) {
          if (data.feeAmountCents < 0) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
          if (isNaN(data.feeAmountCents)) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.planning.validation_errors.field_is_number'
            )
          }
        }

        if (data.budgetType === budgetTypesEnum.totalBudget) {
          if (data.plannedCents !== undefined && data.plannedCents !== null) {
            if (isNaN(data.plannedCents)) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.format_error'
              )
            } else if (data.plannedCents < 0) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
          if (data.plannedMinutes !== undefined && data.plannedMinutes !== null) {
            if (isNaN(data.plannedMinutes)) {
              errorValidation.plannedMinutes = i18n.t(
                'project_management.forms.financial_details.fixed_costs.format_error'
              )
            } else if (data.plannedMinutes < 0) {
              errorValidation.plannedMinutes = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
        }

        return errorValidation
      },
      onSubmit: async (data) => {
        const result = await getMutation(data)

        const handled = handleResult(result, (err) => {
          if (err) setErrors(err)
        })

        if (handled.success) {
          const trackBudgetAction = () => {
            const currentBudgetType = getCurrentBudgetType()
            const targetBudgetType = data.budgetType

            // Handle deletion case
            if (targetBudgetType === budgetTypesEnum.withoutBudget) {
              if (currentBudgetType !== budgetTypesEnum.withoutBudget) {
                track(trackingEvents.projectManagement.settings.deleteBudget)
              }
              return
            }

            // Get tracking budget type name
            const getTrackingBudgetType = (budgetType: string) => {
              switch (budgetType) {
                case budgetTypesEnum.projectFixedCost:
                  return project.isBillable ? 'Planned_cost_income' : 'Planned_cost'
                case budgetTypesEnum.totalBudget:
                  return 'total budget'
                default:
                  return budgetType
              }
            }

            const trackingBudgetType = getTrackingBudgetType(targetBudgetType)
            const isUpdate = currentBudgetType === targetBudgetType
            const isCreate = currentBudgetType === budgetTypesEnum.withoutBudget

            // Determine tracking parameters
            const trackingParams: Record<string, string> = {
              budgetType: trackingBudgetType,
            }

            // Add specific parameters for fixed cost planning
            if (targetBudgetType === budgetTypesEnum.projectFixedCost) {
              trackingParams.income = data.feeAmountCents ? 'yes' : 'no'

              // Add amountChanged for updates
              if (
                isUpdate &&
                project.budgetStrategy &&
                project.budgetStrategy.plannedCents &&
                project.budgetStrategy.budgetType === budgetTypesEnum.projectFixedCost
              ) {
                trackingParams.amountChanged =
                  data.plannedCents !== project.budgetStrategy.plannedCents / 100 ? 'yes' : 'no'
              } else if (!isCreate) {
                trackingParams.amountChanged = 'yes'
              }
            } else if (isUpdate && !isCreate) {
              // For other budget types, always mark amount as changed when updating
              trackingParams.amountChanged = 'yes'
            }

            // Track the appropriate event
            if (isCreate) {
              track(trackingEvents.projectManagement.settings.addBudget, trackingParams)
            } else {
              track(trackingEvents.projectManagement.settings.updateBudget, trackingParams)
            }
          }

          trackBudgetAction()

          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        return handled
      },
    })

  const handleFieldChanged = (
    fieldName: string,
    fieldValue: number | string | undefined,
    shouldValidate = false
  ) => {
    const newValues = { ...values, [fieldName]: fieldValue }

    if (fieldName === 'budgetType') {
      newValues['budgetAllocation'] = undefined
      newValues['feeAmountCents'] = undefined
      newValues['plannedCents'] = undefined
      newValues['plannedMinutes'] = undefined

      setErrors({})
    }

    setValues(newValues, shouldValidate)
  }

  return (
    <Section.Form
      illustration="money"
      title={i18n.t('project_management.planning.title')}
      description={i18n.t('project_management.planning.description')}
      onSubmit={handleSubmit}
      submitLabel={i18n.t('common.submit')}
      isDirty={dirty}
      paddingX="s0"
    >
      <ProjectBudgetForm
        onChangeValue={handleFieldChanged}
        values={values}
        errors={errors}
        isBillable={project.isBillable ?? false}
        isOwner={!!project._policies?.assignOwner.can}
        hasCostsFeature={hasCostsFeature}
        hasBudgetFeature
      />
    </Section.Form>
  )
}
